import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid2';
import { Box, Card, Stack, Typography } from '@mui/material';
import { IconCircleFilled, IconX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { API_GROUP } from '../../../api/user';
import { setNewGameBets } from '../../../store/apps/freeSpinSlice';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../../store';
import useToast from '../../../hooks/useToast';
import CusLabelSelect from '../../../components/material-ui/CusLabelSelect';
import CusInput from '../../../components/material-ui/CusInput';
import ShowContainer from '../../../components/material-ui/ShowContainer';
import CusButton from '../../../components/material-ui/CusButton';
import { ENUM_FS_MODE_STATUS, TEST_MODE } from '../../../enums/enums';
import InfoDialog from '../../../components/shared/InfoDialog';
import CusTextarea from '../../../components/material-ui/CusTextarea';
import useColor from '../../../hooks/site/useColor';
import { API_INFO } from '../../../api/info';
import CusTooltip from '../../../components/material-ui/CusTooltip';

const optionsCountFS = (() => {
  const res = [];
  for (let i = 1; i <= 100; i++) {
    res.push(i);
  }

  return res.map((el) => ({ label: el, value: el }));
})();

export default function FSGameCard({ game, groupId, onCrossClick, changeGameField, currency }) {
  const { errorToast } = useToast();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { success, error } = useColor();
  const gameBets = useAppSelector((state) => state.freeSpin.gameBets?.[groupId]) || {};
  const providers = useAppSelector((state) => state.freeSpin.providers?.[groupId]) || [];

  const successColor = success.main;
  const errorColor = error.main;

  const [fsMode, setFsMode] = useState(game.status);
  const [unavailableText, setUnavailableText] = useState('');
  const [btnCheckFsDisabled, setBtnCheckFsDisabled] = useState(false);

  const gameId = game.gameId;
  const gameBetId = game.bet?.betId;
  const gameWager = game.wager;
  const gameFreespins = game.freespins;
  const gameCashout = game.cashout;

  const currentBets = gameBets?.[gameId];
  const totalBestOptions = currentBets?.map((bet) => {
    const totalBet = bet.lines
      ? ` ${bet.totalBet} ( ${t('countLines', { lines: bet.lines })} )`
      : bet.totalBet;

    return {
      value: +bet.betId,
      label: totalBet
    };
  });

  const gameRowSize = { xs: 12, sm: 6 };
  const styleCross = { position: 'absolute', right: '12px', top: '12px', cursor: 'pointer' };
  const isAvailableMode = fsMode === ENUM_FS_MODE_STATUS.available;

  const getBets = async (gameId) => {
    try {
      const res = await API_GROUP.bonus.freespin.geBetsGames({ gameId, currency });
      const bets = res.data.bets;
      if (!bets) return;
      if (!bets.length) {
        onCrossClick(game);
        errorToast("You can't add a game because there are no bets on it.");
        return;
      }
      dispatch(setNewGameBets({ groupId, gameId, bets }));
    } catch (e) {
      console.error(e);

      onCrossClick(game);
      errorToast('This game cannot be added');
      return undefined;
    }
  };

  const onCheckClick = async () => {
    setBtnCheckFsDisabled(true);
    const betLines = currentBets?.find((el) => el.betId == gameBetId)?.lines;

    API_INFO.freespinTest({
      gameId,
      currency,
      fsCount: gameFreespins,
      bet: { betId: gameBetId, lines: betLines }
    })
      .then((e) => {
        const result = e.data.result;
        const status = result.ok ? ENUM_FS_MODE_STATUS.available : ENUM_FS_MODE_STATUS.unavailable;

        changeGameField(gameId, status, 'status');
        setFsMode(status);
        if (status === ENUM_FS_MODE_STATUS.unavailable) {
          errorToast(t('Try changing the currency or choosing a different provider/game'));
        }
        if (!result.ok && result.reply) {
          setUnavailableText(result.reply);
        }
      })
      .catch(console.log)
      .finally(() => setBtnCheckFsDisabled(false));
  };

  useEffect(() => {
    if (TEST_MODE) return;

    // if (totalBets?.length) return;
    if (gameId) getBets(gameId);
  }, [gameId, currency]);

  useEffect(() => {
    setFsMode(ENUM_FS_MODE_STATUS.wait);
    changeGameField(gameId, ENUM_FS_MODE_STATUS.wait, 'status');
  }, [gameId, gameBetId, gameWager, gameFreespins, gameCashout, currency]);

  const providerName = providers.find((el) => el.id == game.provider)?.providerName;

  return (
    <>
      <Grid key={gameId} size={{ xs: 12, sm: 6 }}>
        <Card variant="outlined" style={{ position: 'relative' }}>
          <Stack display={'flex'} flexDirection={'column'} gap={1}>
            <IconX size={28} style={styleCross} onClick={() => onCrossClick(game)} />

            <Stack display={'inline'} alignItems={'center'} gap={1} mb={1}>
              <Typography variant="h5" style={{ display: 'contents' }}>
                {game.name}
              </Typography>
              <ShowContainer condition={providerName}>
                <Typography variant="caption" color="info" style={{ marginLeft: '4px' }}>
                  ({providerName})
                </Typography>
              </ShowContainer>
            </Stack>
            <ShowContainer condition={game.uuid}>
              <Typography variant="caption" mb={1} color="info">
                uuid: {game.uuid}
              </Typography>
            </ShowContainer>

            <Grid size={12} container spacing={1}>
              <Grid size={gameRowSize}>
                <CusLabelSelect
                  id={'freespins'}
                  value={gameFreespins}
                  options={optionsCountFS}
                  label={'number of freespins'}
                  onChange={(val) => changeGameField(gameId, val, 'freespins')}
                />
              </Grid>
              <Grid size={gameRowSize}>
                <CusLabelSelect
                  id={'betId'}
                  value={gameBetId}
                  options={totalBestOptions}
                  label={'Total Bet'}
                  onChange={(val) => changeGameField(gameId, val, 'betId')}
                />
              </Grid>
              <Grid size={gameRowSize}>
                <CusInput
                  value={gameWager}
                  label={'Wager'}
                  onChange={(e) => changeGameField(gameId, Math.abs(e.target.value), 'wager')}
                  type="number"
                  inputProps={{ min: 0, max: 1_000 }}
                />
              </Grid>
              <Grid size={gameRowSize}>
                <CusInput
                  value={gameCashout}
                  label={'Cashout'}
                  onChange={(e) => changeGameField(gameId, Math.abs(e.target.value), 'cashout')}
                  type="number"
                  inputProps={{ min: 0, max: 1_000 }}
                />
              </Grid>
              <Grid size={gameRowSize}>
                <Box>
                  <Typography>
                    {t('Freespins mode')}
                    <IconCircleFilled
                      height={14}
                      fill={isAvailableMode ? successColor : errorColor}
                      color={isAvailableMode ? successColor : errorColor}
                      style={{ color: isAvailableMode ? successColor : errorColor }}
                    />{' '}
                  </Typography>
                  <Typography fontWeight={600} color={isAvailableMode ? 'success' : 'error'}>
                    <Box
                      component="span"
                      ml={0.5}
                      display={'inline-flex'}
                      alignItems={'center'}
                      gap={0.5}>
                      {t(fsMode)}
                      <ShowContainer condition={ENUM_FS_MODE_STATUS.unavailable === fsMode}>
                        <CusTooltip
                          text={'Try changing the currency or choosing a different provider/game'}
                        />
                      </ShowContainer>
                    </Box>
                  </Typography>
                </Box>
              </Grid>
              <Grid size={gameRowSize}>
                <ShowContainer condition={!isAvailableMode}>
                  <CusButton
                    fullWidth
                    variant="outlined"
                    text="Check FS mode"
                    disabled={
                      btnCheckFsDisabled || !gameFreespins || (!gameBetId && gameBetId !== 0)
                    }
                    onClick={onCheckClick}
                  />
                </ShowContainer>
              </Grid>
            </Grid>
          </Stack>
        </Card>
      </Grid>

      <InfoDialog
        open={!!unavailableText}
        onClose={() => setUnavailableText(false)}
        title={'Freespins mode is not available'}
        infoJSX={
          <Card>
            {/* <CusTextarea hideToolbar readOnly value={unavailableText} /> */}

            <pre
              style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: '50svh',
                overflow: 'auto'
              }}>
              {JSON.stringify(unavailableText, null, 2)}
            </pre>
          </Card>
        }
      />
    </>
  );
}
